// ============================================================================
// dataTypes/liveStreamManagment/audioPipeline/audioMeter.ts
//
// API de metering audio — factory pattern, un état par instance.
//
// Chaque source du deck crée sa propre instance via createMeter().
// Les fonctions de mesure travaillent sur des Float32Array de samples
// temporels (getFloatTimeDomainData).
//
// Types disponibles :
//   peak — valeur instantanée, pas d'état
//   rms  — RMS sur la fenêtre courante, pas d'état
//   vu   — lissage exponentiel montée/descente séparés (VU-mètre analogique)
//   ppm  — pic instantané + retombée à vitesse constante en dB/s (PPM broadcast)
//
// Valeurs par défaut communes :
//   Plage dB        : -60 à +6
//   VU lissage montée   : 0.3
//   VU lissage descente : 0.05
//   PPM retombée        : 20 dB/s
//
// 2026-10-23 : la retombée du PPM était un facteur 0.92 PAR FRAME, soit ~43 dB/s à
// 60 i/s et le double sur un écran à 120 Hz. Elle se mesure désormais au temps
// écoulé, indépendamment de la cadence d'affichage.
// ============================================================================

// ─── Types ───────────────────────────────────────────────────────────────────

export type MeterType = 'peak' | 'rms' | 'vu' | 'ppm';

export interface MeterLevels {
  left:  number; // dB, -60 à +6
  right: number; // dB, -60 à +6
}

export interface MeterOptions {
  /** Valeur plancher en dB (défaut : -60) */
  dbFloor?: number;
  /** Valeur plafond en dB (défaut : +6) */
  dbCeil?: number;
  /** VU — coefficient de lissage à la montée, 0–1 (défaut : 0.3) */
  vuAttack?: number;
  /** VU — coefficient de lissage à la descente, 0–1 (défaut : 0.05) */
  vuRelease?: number;
  /** PPM — vitesse de retombée en dB par seconde (défaut : 20) */
  ppmFallDbPerSec?: number;
}

export interface MeterInstance {
  /** Mesure les niveaux L/R depuis les données temporelles et un gain linéaire */
  measure: (dataL: Float32Array, dataR: Float32Array, gain: number) => MeterLevels;
  /** Remet l'état interne à zéro (utile au reconnect) */
  reset: () => void;
  /** Type de mesure de cette instance */
  type: MeterType;
}

// ─── Constantes par défaut ────────────────────────────────────────────────────

const DEFAULTS: Required<MeterOptions> = {
  dbFloor   : -60,
  dbCeil    :   6,
  vuAttack  :   0.3,
  vuRelease :   0.05,
  ppmFallDbPerSec : 20,
};

/** Au-delà, une pause (onglet caché, première mesure) ne fait pas tomber le PPM d'un coup. */
const PPM_MAX_STEP_MS = 100;

// ─── Utilitaire dB ────────────────────────────────────────────────────────────

function toDb(value: number, floor: number, ceil: number): number {
  const db = 20 * Math.log10(value + 1e-9);
  return Math.max(floor, Math.min(ceil, db));
}

// ─── Factory ──────────────────────────────────────────────────────────────────

/**
 * Crée une instance de mètre audio avec son propre état interne.
 * À appeler une fois par source au moment de la connexion.
 *
 * @param type     Type de mesure ('peak' | 'rms' | 'vu' | 'ppm')
 * @param options  Surcharge des valeurs par défaut
 */
export function createMeter(type: MeterType, options?: MeterOptions): MeterInstance {
  const opts: Required<MeterOptions> = { ...DEFAULTS, ...options };

  // ── État interne (VU et PPM uniquement) ──────────────────────────────────
  let vuLeft   = 0;
  let vuRight  = 0;
  let ppmLeft  = 0;
  let ppmRight = 0;
  let ppmLastMs: number | null = null;

  // ── Fonctions de mesure ───────────────────────────────────────────────────

  function peakMeter(dataL: Float32Array, dataR: Float32Array, gain: number): MeterLevels {
    let left = 0, right = 0;
    for (let i = 0; i < dataL.length; i++) {
      const l = Math.abs(dataL[i] * gain);
      const r = Math.abs(dataR[i] * gain);
      if (l > left)  left  = l;
      if (r > right) right = r;
    }
    return {
      left:  toDb(left,  opts.dbFloor, opts.dbCeil),
      right: toDb(right, opts.dbFloor, opts.dbCeil),
    };
  }

  function rmsMeter(dataL: Float32Array, dataR: Float32Array, gain: number): MeterLevels {
    let sumL = 0, sumR = 0;
    for (let i = 0; i < dataL.length; i++) {
      sumL += (dataL[i] * gain) ** 2;
      sumR += (dataR[i] * gain) ** 2;
    }
    const n = dataL.length || 1;
    return {
      left:  toDb(Math.sqrt(sumL / n), opts.dbFloor, opts.dbCeil),
      right: toDb(Math.sqrt(sumR / n), opts.dbFloor, opts.dbCeil),
    };
  }

  function vuMeterFn(dataL: Float32Array, dataR: Float32Array, gain: number): MeterLevels {
    // RMS instantané
    let sumL = 0, sumR = 0;
    for (let i = 0; i < dataL.length; i++) {
      sumL += (dataL[i] * gain) ** 2;
      sumR += (dataR[i] * gain) ** 2;
    }
    const n = dataL.length || 1;
    const targetL = Math.sqrt(sumL / n);
    const targetR = Math.sqrt(sumR / n);

    // Lissage exponentiel avec attaque/release séparés
    const kAttackL  = targetL > vuLeft  ? opts.vuAttack  : opts.vuRelease;
    const kAttackR  = targetR > vuRight ? opts.vuAttack  : opts.vuRelease;
    vuLeft  = vuLeft  * (1 - kAttackL)  + targetL * kAttackL;
    vuRight = vuRight * (1 - kAttackR) + targetR * kAttackR;

    return {
      left:  toDb(vuLeft,  opts.dbFloor, opts.dbCeil),
      right: toDb(vuRight, opts.dbFloor, opts.dbCeil),
    };
  }

  function ppmMeterFn(dataL: Float32Array, dataR: Float32Array, gain: number): MeterLevels {
    // Pic instantané
    let peakL = 0, peakR = 0;
    for (let i = 0; i < dataL.length; i++) {
      const l = Math.abs(dataL[i] * gain);
      const r = Math.abs(dataR[i] * gain);
      if (l > peakL) peakL = l;
      if (r > peakR) peakR = r;
    }

    // Montée instantanée, retombée de ppmFallDbPerSec dB par seconde écoulée
    const now   = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const stepS = ppmLastMs === null ? 0 : Math.min(now - ppmLastMs, PPM_MAX_STEP_MS) / 1000;
    ppmLastMs   = now;
    const decay = Math.pow(10, -(opts.ppmFallDbPerSec * stepS) / 20);
    ppmLeft  = Math.max(peakL, ppmLeft  * decay);
    ppmRight = Math.max(peakR, ppmRight * decay);

    return {
      left:  toDb(ppmLeft,  opts.dbFloor, opts.dbCeil),
      right: toDb(ppmRight, opts.dbFloor, opts.dbCeil),
    };
  }

  // ── Dispatch ──────────────────────────────────────────────────────────────

  function measure(dataL: Float32Array, dataR: Float32Array, gain: number): MeterLevels {
    switch (type) {
      case 'peak': return peakMeter(dataL,   dataR,  gain);
      case 'rms':  return rmsMeter(dataL,    dataR,  gain);
      case 'vu':   return vuMeterFn(dataL,   dataR,  gain);
      case 'ppm':  return ppmMeterFn(dataL,  dataR,  gain);
      default:     return { left: opts.dbFloor, right: opts.dbFloor };
    }
  }

  function reset(): void {
    vuLeft = vuRight = ppmLeft = ppmRight = 0;
    ppmLastMs = null;
  }

  return { measure, reset, type };
}